import { useCallback, useEffect, useRef, useState } from "react";

import {
  type DashboardCreditTransaction,
  createBillingPortalSessionAsUser,
  createTopupSessionAsUser,
  getBillingBalanceAsUser,
  listBillingTransactionsAsUser,
  setUpOrganizationBillingAsUser,
} from "../dashboard-client";
import { useDashboardRealtime } from "./useDashboardRealtime";

interface UseBillingState {
  balance: number | null;
  hasBillingProfile: boolean;
  transactions: DashboardCreditTransaction[];
  loading: boolean;
  error: string | null;
}

const TERMINAL_TASK_STATUSES = new Set([
  "completed",
  "succeeded",
  "failed",
  "error",
  "cancelled",
  "canceled",
  "skipped",
]);

function isTerminalTaskStatus(value: unknown): boolean {
  return (
    typeof value === "string" &&
    TERMINAL_TASK_STATUSES.has(value.trim().toLowerCase())
  );
}

export function useBilling(organizationId: string | null) {
  const [state, setState] = useState<UseBillingState>({
    balance: null,
    error: null,
    hasBillingProfile: false,
    loading: false,
    transactions: [],
  });
  const refreshTimeoutRef = useRef<number | null>(null);
  const { subscribe } = useDashboardRealtime({
    enabled: Boolean(organizationId),
  });

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setState({
        balance: null,
        error: null,
        hasBillingProfile: false,
        loading: false,
        transactions: [],
      });
      return;
    }
    setState((s) => ({ ...s, error: null, loading: true }));
    try {
      const [balanceData, txData] = await Promise.all([
        getBillingBalanceAsUser({ path: { organizationId } }).then(
          (r) => r.data,
        ),
        listBillingTransactionsAsUser({
          path: { organizationId },
          query: { limit: 20 },
        }).then((r) => r.data),
      ]);
      setState({
        balance: balanceData.balanceUsd,
        error: null,
        hasBillingProfile: balanceData.hasBillingProfile,
        loading: false,
        transactions: txData.items,
      });
    } catch (error) {
      setState((s) => ({
        ...s,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load billing data",
        loading: false,
      }));
    }
  }, [organizationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current !== null) {
      return;
    }
    refreshTimeoutRef.current = window.setTimeout(() => {
      refreshTimeoutRef.current = null;
      void refresh();
    }, 250);
  }, [refresh]);

  useEffect(
    () => () => {
      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!organizationId) {
      return () => {
        // Ignored
      };
    }

    const unsubscribe = subscribe(
      (message) => {
        if (message.type === "task_callback") {
          const { payload } = message;
          if (!isTerminalTaskStatus(payload.status)) {
            return;
          }
          const { task } = payload;
          const eventOrgId =
            task &&
            typeof task === "object" &&
            "organization_id" in task &&
            typeof task.organization_id === "string"
              ? task.organization_id
              : null;
          if (eventOrgId && eventOrgId !== organizationId) {
            return;
          }
          scheduleRefresh();
        }
      },
      { topic: "internalEventTopic" },
    );

    return () => {
      unsubscribe();
      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [organizationId, scheduleRefresh, subscribe]);

  const setup = useCallback(async () => {
    if (!organizationId) {
      return;
    }
    await setUpOrganizationBillingAsUser({ path: { organizationId } });
    await refresh();
  }, [organizationId, refresh]);

  const topUp = useCallback(
    async (amountCents: number) => {
      if (!organizationId) {
        return;
      }
      await setUpOrganizationBillingAsUser({ path: { organizationId } });
      const { data } = await createTopupSessionAsUser({
        body: { amountCents },
        path: { organizationId },
      });
      window.location.href = data.sessionUrl;
    },
    [organizationId],
  );

  const openPortal = useCallback(async () => {
    if (!organizationId) {
      return;
    }
    const { data } = await createBillingPortalSessionAsUser({
      body: { returnUrl: window.location.href },
      path: { organizationId },
    });
    window.location.href = data.portalUrl;
  }, [organizationId]);

  return {
    balance: state.balance,
    error: state.error,
    hasBillingProfile: state.hasBillingProfile,
    loading: state.loading,
    openPortal,
    refresh,
    setup,
    topUp,
    transactions: state.transactions,
  };
}
